import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { WOLF_HEIGHT, WOLF_VISUAL_HEIGHT, WOLF_BODY_RISE, wolfLegHeight, wolfJoints } from '../../../content/characters/rigs/wolf.js';
import type { UnitDefinition } from '../../../shared/game/world-types.js';

export interface WolfRig { root: THREE.Group; joints: Map<string, THREE.Bone>; skeleton: THREE.Skeleton; height: number }
type Ring = [number, number, number, number, number, string, string?, number?];
/** Which part of the coat a loft paints: the grey saddle and cream belly, tan legs, a dark-tipped brush, pale jaw or dark-backed ears. */
type Coat = 'body' | 'leg' | 'tail' | 'jaw' | 'ear';
type Weights = readonly (readonly [string, number])[];
const ramp = THREE.MathUtils.smoothstep;
/** Linear-light grey for one vertex. `top` is -1 underneath to 1 on top; `along` runs 0–1 down the loft. */
function coatColour(coat: Coat, z: number, top: number, along: number, wobble: number): [number, number, number] {
  const underside = ramp(-top, -.15, .8), dorsal = ramp(top, .05, .95);
  let tone: number;
  if (coat === 'body') {
    // A charcoal saddle over the shoulders and back fades to silver on the flanks, rump and neck; belly, throat and muzzle are cream.
    const saddle = ramp(z, -.8, -.3) * (1 - ramp(z, .62, 1.0)), muzzle = ramp(z, 1.02, 1.30), bridge = ramp(z, .95, 1.1) * (1 - muzzle) * dorsal;
    const flank = THREE.MathUtils.lerp(.21, .085, saddle * (.35 + .65 * dorsal)) - bridge * .04;
    tone = THREE.MathUtils.lerp(flank + underside * .40, .6 + underside * .1, muzzle);
  } else if (coat === 'leg') tone = THREE.MathUtils.lerp(.2, .3, ramp(along, .2, .7)) + underside * .05;
  else if (coat === 'tail') tone = THREE.MathUtils.lerp(.17, .24, ramp(along, 0, .5)) + underside * .22 * (1 - ramp(along, .35, .6)) - .03 * dorsal - .2 * ramp(along, .72, .95);
  else if (coat === 'jaw') tone = .6;
  else tone = .2 - .12 * ramp(along, .7, 1);
  tone = Math.max(.02, tone + wobble);
  // Dark coat is warm brown-grey, pale coat is cream.
  const warmth = 1 - ramp(tone, .28, .6);
  return [tone * (1 + .05 * warmth), tone * (1 + .005 * warmth), tone * (1 - .05 * warmth)];
}

/** UV fur grain follows each loft; coat markings are vertex colours and survive texture replacement. */
export function wolfFurTexture() {
  const size = 256, pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const strand = Math.sin(x * .74 + Math.sin(y * .085) * 1.8 + Math.sin(x * .13) * 2) * (.35 + .65 * Math.sin(y * .19 + x * .07) ** 2);
    const grain = ((x * 37 + y * 71 + (x * y) % 97) % 101) / 101;
    const value = Math.round(230 + 5 * strand + 5 * grain);
    pixels.set([value, value, value, 255], (y * size + x) * 4);
  }
  const texture = new THREE.DataTexture(pixels, size, size);
  texture.colorSpace = THREE.SRGBColorSpace; texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter; texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true; texture.needsUpdate = true; texture.name = 'wolf_fur';
  return texture;
}

/** Smooth weighted surfaces, rather than a humanoid skeleton with four reassigned limbs. */
export function createWolfModel(definition: UnitDefinition, textured = true): WolfRig {
  const root = new THREE.Group(); root.name = 'wolf'; root.userData.nimblezCharacter = 'wolf';
  root.userData.nimblezHeight = WOLF_HEIGHT; root.userData.nimblezVersion = 2;
  const joints = new Map<string, THREE.Bone>(), points = new Map<string, THREE.Vector3>();
  for (const [name, parent, x, y, z] of wolfJoints) {
    const bone = new THREE.Bone(); bone.name = name;
    if (name === 'root' || name === 'body') bone.userData.nimblezControlBone = true;
    const point = new THREE.Vector3(x, y, z); points.set(name, point);
    bone.position.copy(point).sub(parent ? points.get(parent)! : new THREE.Vector3());
    (parent ? joints.get(parent)! : root).add(bone); joints.set(name, bone);
  }
  root.updateMatrixWorld(true);
  const bones = [...joints.values()], skeleton = new THREE.Skeleton(bones);
  const fur = new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: .95, map: textured ? wolfFurTexture() : null }); fur.name = 'wolf_coat';
  const look = definition.appearance; if (look?.coat !== undefined) fur.color.setHex(look.coat);
  const black = new THREE.MeshStandardMaterial({ color: 0x171b1d, roughness: .55 }); black.name = 'wolf_nose';
  const eye = new THREE.MeshStandardMaterial({ color: 0xc69a3c, roughness: .3 }); eye.name = 'wolf_eyes';
  if (look?.eyes !== undefined) { eye.color.setHex(look.eyes); eye.emissive.setHex(look.eyes); eye.emissiveIntensity = .9; }
  const teeth = new THREE.MeshStandardMaterial({ color: 0xede4cf, roughness: .55 }); teeth.name = 'wolf_teeth';
  const mouth = new THREE.MeshStandardMaterial({ color: 0x3a2528, roughness: .85 }); mouth.name = 'wolf_mouth';
  const indices = new Map(bones.map((bone, i) => [bone.name, i]));

  function skin(geometry: THREE.BufferGeometry, material: THREE.Material, weights: (p: THREE.Vector3, i: number) => Weights, name: string) {
    const attribute = geometry.getAttribute('position'), jointIds: number[] = [], values: number[] = [];
    if (name.startsWith('foreleg_') || name.startsWith('hindleg_')) {
      for (let i = 0; i < attribute.count; i++) attribute.setY(i, wolfLegHeight(attribute.getY(i)));
      geometry.computeVertexNormals();
    } else if (!/^(front|hind)_(paw|toe|claw)_/.test(name)) geometry.translate(0, WOLF_BODY_RISE, 0);
    for (let i = 0; i < attribute.count; i++) {
      const influences = weights(new THREE.Vector3().fromBufferAttribute(attribute, i), i);
      for (let joint = 0; joint < 4; joint++) {
        jointIds.push(influences[joint] ? indices.get(influences[joint][0])! : 0); values.push(influences[joint]?.[1] ?? 0);
      }
    }
    geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(jointIds, 4));
    geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(values, 4));
    const mesh = new THREE.SkinnedMesh(geometry, material); mesh.name = name;
    mesh.castShadow = mesh.receiveShadow = true; mesh.frustumCulled = false;
    root.add(mesh); mesh.bind(skeleton); mesh.userData.nimblezPart = 'body'; return mesh;
  }
  function loft(name: string, rings: Ring[], coat: Coat = 'body', segments = 20) {
    const positions: number[] = [], colors: number[] = [], uvs: number[] = [], faces: number[] = [];
    const weights: Weights[] = [];
    const centers = new THREE.CatmullRomCurve3(rings.map(ring => new THREE.Vector3(ring[0], ring[1], ring[2])), false, 'catmullrom', .35);
    const radii = new THREE.CatmullRomCurve3(rings.map(ring => new THREE.Vector3(ring[3], ring[4], 0)), false, 'catmullrom', .35);
    const rows = (rings.length - 1) * (coat === 'ear' ? 1 : 3);
    for (let row = 0; row <= rows; row++) {
      const u = row / rows, center = centers.getPoint(u), radius = radii.getPoint(u);
      const { x, y, z } = center, rx = Math.max(.001, radius.x), ry = Math.max(.001, radius.y);
      const tangent = centers.getTangent(u);
      const across = new THREE.Vector3(1, 0, 0), vertical = tangent.clone().cross(across).normalize();
      // Interpolating the neighbouring rings' influences preserves the original joints through the smoother silhouette.
      const source = u * (rings.length - 1), at = Math.min(rings.length - 2, Math.floor(source)), blend = source - at;
      const influence = new Map<string, number>();
      for (const [ring, amount] of [[rings[at], 1 - blend], [rings[at + 1], blend]] as const) {
        const a = ring[5], b = ring[6] ?? a, t = ring[7] ?? 0;
        influence.set(a, (influence.get(a) ?? 0) + amount * (1 - t)); influence.set(b, (influence.get(b) ?? 0) + amount * t);
      }
      const weighted = [...influence].filter(([, weight]) => weight > 0);
      for (let i = 0; i <= segments; i++) {
        const angle = i / segments * Math.PI * 2, top = Math.sin(angle);
        positions.push(x + Math.cos(angle) * rx, y + top * ry * vertical.y, z + top * ry * vertical.z);
        const [red, green, blue] = coatColour(coat, z, coat === 'body' || coat === 'tail' ? top * vertical.y : top, u, .004 * Math.sin(x * 37 + y * 23 + z * 29));
        colors.push(red, green, blue);
        uvs.push(i / segments * 3, u * 2); weights.push(weighted);
        if (row && i < segments) {
          const k = row * (segments + 1) + i, p = k - segments - 1;
          faces.push(p, p + 1, k, p + 1, k + 1, k);
        }
      }
    }
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3)); geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(faces); geometry.computeVertexNormals(); skin(geometry, fur, (_, i) => weights[i], name);
  }
  function ellipsoid(name: string, joint: string, center: number[], scale: number[], material = fur, tone = .27) {
    const small = Math.max(...scale) < .035;
    const geometry = new THREE.SphereGeometry(1, small ? 8 : 12, small ? 6 : 8); geometry.scale(scale[0], scale[1], scale[2]); geometry.translate(center[0], center[1], center[2]);
    if (material === fur) {
      const colors: number[] = []; for (let i = 0; i < geometry.getAttribute('position').count; i++) colors.push(tone, tone * .97, tone * .9);
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    }
    skin(geometry, material, () => [[joint, 1]], name);
  }
  // Full haunches, a tucked waist, a deep chest that reaches the elbows, and a heavy ruffed neck carrying a broad skull.
  const bodyRings: Ring[] = [
    [0,.83,-.83,.01,.015,'pelvis'], [0,.83,-.71,.245,.27,'pelvis'], [0,.86,-.48,.30,.31,'pelvis','spine_lumbar',.2],
    [0,.89,-.23,.215,.215,'spine_lumbar'], [0,.90,0,.255,.26,'spine_lumbar','spine_thoracic',.75],
    [0,.90,.23,.305,.33,'spine_thoracic','chest',.45], [0,.92,.43,.30,.35,'chest'],
    [0,.98,.59,.27,.32,'chest','neck',.25], [0,1.10,.73,.23,.26,'neck'],
    [0,1.22,.86,.19,.2,'neck','head',.8], [0,1.245,.98,.185,.17,'head'],
    [0,1.22,1.10,.15,.125,'head'], [0,1.18,1.27,.1,.07,'head'],
    [0,1.16,1.4,.07,.05,'head'], [0,1.16,1.435,.01,.02,'head'],
  ];
  loft('coat_body', bodyRings);
  /** Height of the top of the torso at `z`, for rooting the mane. */
  const backAt = (z: number) => {
    for (let i = 1; i < bodyRings.length; i++) {
      const [, y0, z0, , r0] = bodyRings[i - 1], [, y1, z1, , r1] = bodyRings[i];
      if (z <= z1 && z >= z0) { const t = (z - z0) / (z1 - z0); return THREE.MathUtils.lerp(y0 + r0, y1 + r1, t); }
    }
    return 1;
  };
  /** A tapered fur lock rooted at `at`, pointing along `toward`, dark at the root and lighter (or darker) at the tip. */
  function tuft(joint: string, at: number[], toward: number[], length: number, radius: number, tones: [number, number], name = 'tuft', weights: Weights = [[joint, 1]]) {
    const geometry = new THREE.ConeGeometry(radius, length, 6, 1); geometry.translate(0, length / 2, 0);
    const position = geometry.getAttribute('position'), colors: number[] = [];
    for (let i = 0; i < position.count; i++) {
      const tone = THREE.MathUtils.lerp(tones[0], tones[1], position.getY(i) / length);
      colors.push(tone * 1.04, tone, tone * .93);
    }
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(...toward).normalize()));
    geometry.translate(at[0], at[1], at[2]); skin(geometry, fur, () => weights, name);
  }
  // Mane: overlapping rows of short silver-tipped locks lying back from the nape over the shoulders, fading out along the spine.
  for (let z = .9, index = 0; z > -.1; z -= .065, index++) {
    // Fur roots follow the same blended spine sections as the coat beneath them.
    const at = bodyRings.findIndex(ring => ring[2] >= z), left = bodyRings[at - 1], right = bodyRings[at];
    const t = (z - left[2]) / (right[2] - left[2]), influence = new Map<string, number>();
    for (const [ring, amount] of [[left, 1 - t], [right, t]] as const) {
      const mix = ring[7] ?? 0;
      influence.set(ring[5], (influence.get(ring[5]) ?? 0) + amount * (1 - mix));
      if (ring[6]) influence.set(ring[6], (influence.get(ring[6]) ?? 0) + amount * mix);
    }
    const weights = [...influence], joint = weights[0][0], fade = .45 + .55 * ramp(z, -.1, .35), length = (.13 - Math.abs(z - .6) * .04) * fade, spread = z > .5 ? .075 : .05;
    tuft(joint, [0, backAt(z) - .05, z], [0, .08, -1], length * .8, .045, [.1, .25], `mane_${index}`, weights);
    for (const sign of [-1, 1]) tuft(joint, [sign * spread, backAt(z) - .075 - spread * .6, z + .01], [sign * .55, -.02, -1], length * .85, .04, [.12, .27], `mane_${index}_${sign}`, weights);
  }
  loft('lower_jaw', [[0,1.12,.98,.04,.045,'jaw'],[0,1.10,1.12,.115,.055,'jaw'],[0,1.09,1.28,.087,.04,'jaw'],[0,1.10,1.4,.065,.028,'jaw'],[0,1.10,1.42,.01,.01,'jaw']], 'jaw');
  ellipsoid('nose','head',[0,1.17,1.421],[.069,.043,.037],black);
  ellipsoid('mouth_interior','jaw',[0,1.13,1.26],[.087,.017,.145],mouth);
  for (const side of ['L','R']) {
    const sign = side === 'L' ? -1 : 1, x = sign * .235;
    // Backward elbows, forward knees and raised hocks make the canid silhouette.
    loft(`foreleg_${side}`, [[x,.94,.40,.025,.05,`shoulder_${side}`],[x,.77,.40,.092,.15,`shoulder_${side}`],
      [x,.56,.385,.069,.115,`shoulder_${side}`,`elbow_${side}`,.5],[x,.48,.37,.058,.078,`elbow_${side}`],
      [x,.31,.42,.043,.095,`elbow_${side}`,`wrist_${side}`,.5],[x,.15,.48,.037,.065,`wrist_${side}`],
      [x,.075,.57,.025,.02,`front_paw_${side}`]],'leg');
    loft(`hindleg_${side}`, [[x,.86,-.55,.035,.06,`hip_${side}`],[x,.71,-.51,.155,.205,`hip_${side}`],
      [x,.52,-.34,.12,.15,`hip_${side}`,`knee_${side}`,.7],[x,.48,-.29,.085,.10,`knee_${side}`],
      [x,.35,-.49,.067,.095,`knee_${side}`,`hock_${side}`,.5],[x,.25,-.64,.051,.075,`hock_${side}`],
      [x,.17,-.60,.036,.062,`hock_${side}`,`ankle_${side}`,.5],[x,.105,-.56,.038,.05,`ankle_${side}`],
      [x,.065,-.48,.025,.02,`hind_paw_${side}`]],'leg');
    for (const [which,z] of [['front',.57],['hind',-.48]] as const) {
      ellipsoid(`${which}_paw_${side}`,`${which}_paw_${side}`,[x,.07,z],[.092,.065,.137]);
      for (let toe=0;toe<4;toe++) {
        const tx=x+(toe-1.5)*.038, forward = toe === 0 || toe === 3 ? -.014 : 0;
        ellipsoid(`${which}_toe_${side}_${toe}`,`${which}_paw_${side}`,[tx,.053,z+.09+forward],[.024,.039,.06]);
        ellipsoid(`${which}_claw_${side}_${toe}`,`${which}_paw_${side}`,[tx,.043,z+.144+forward],[.009,.012,.029],black);
      }
    }
    const earX=sign*.14;
    // Narrow lofts form upright tapered ears, weighted separately for alert/fear motion.
    loft(`ear_${side}`,[[earX,1.33,.82,.055,.035,`ear_${side}`],[earX,1.40,.84,.056,.055,`ear_${side}`],
      [earX+sign*.016,1.47,.87,.013,.012,`ear_${side}`],[earX+sign*.016,1.485,.872,.002,.002,`ear_${side}`]],'ear',12);
    // Heavy brows give the face its stare; elbow and hock fringes soften the limbs.
    ellipsoid(`brow_${side}`,'head',[sign*.125,1.31,1.07],[.04,.014,.06],fur,.1);
    // Soft cheek ruff, pale and swept back from the jaw line.
    ellipsoid(`cheek_${side}`,'head',[sign*.145,1.18,.96],[.033,.065,.095],fur,.35);
    for (let lock = 0; lock < 3; lock++) tuft('head', [sign * .158, 1.17 - lock * .025, .95 - lock * .025], [sign * .25, -.25, -1], .09, .026, [.29, .4], `cheek_lock_${side}_${lock}`);
    ellipsoid(`eye_socket_${side}`,'head',[sign*.135,1.272,1.075],[.025,.019,.028],black);
    ellipsoid(`eye_${side}`,'head',[sign*.151,1.274,1.087],[.011,.012,.016],eye);
    ellipsoid(`pupil_${side}`,'head',[sign*.161,1.275,1.093],[.004,.010,.006],black);
    for (const [joint,y] of [['head',1.135],['jaw',1.133]] as const) {
      const geometry=new THREE.ConeGeometry(.014,.048,8); geometry.rotateX(joint==='head'?Math.PI:0); geometry.translate(sign*.073,y,1.25);
      skin(geometry,teeth,()=>[[joint,1]],`canine_${joint}_${side}`);
    }
  }
  const tailRings: Ring[] = [[0,.88,-.73,.08,.08,'tail_1'],[0,.8,-.9,.135,.155,'tail_1','tail_2',.35],
    [0,.69,-1.05,.145,.17,'tail_2'],[0,.5,-1.23,.125,.155,'tail_2','tail_3',.6],
    [0,.36,-1.35,.085,.11,'tail_3'],[0,.31,-1.4,.003,.003,'tail_3']];
  loft('tail', tailRings, 'tail');
  // One skin per material: ears, toes, eyes and teeth must not each cost a draw call.
  for (const material of [fur,black,eye,teeth,mouth]) {
    const meshes=root.children.filter(child=>child instanceof THREE.SkinnedMesh && child.material===material) as THREE.SkinnedMesh[];
    const geometry=mergeGeometries(meshes.map(mesh=>mesh.geometry));
    if(!geometry)throw new Error(`Cannot merge wolf material ${material.name}`);
    const mesh=new THREE.SkinnedMesh(geometry,material);mesh.name=material.name;mesh.userData.nimblezPart='body';
    root.add(mesh);mesh.bind(skeleton);mesh.castShadow=mesh.receiveShadow=true;mesh.frustumCulled=false;
    for(const part of meshes){part.removeFromParent();part.geometry.dispose();}
  }
  const height=definition.appearance?.height??WOLF_VISUAL_HEIGHT; root.scale.setScalar(height/WOLF_HEIGHT);
  root.userData.nimblezHeight = height;
  return {root,joints,skeleton,height};
}
